import { useState, useRef } from "react";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { IvpLogo } from "@/components/IvpLogo";
import { adminLogin, setAdminToken } from "@/lib/api";
import { TurnstileWidget, captchaEnabled } from "@/components/TurnstileWidget";

export const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    if (captchaEnabled && !captchaToken) {
      toast.error("Please complete the human verification");
      return;
    }
    setLoading(true);
    try {
      const { token } = await adminLogin(password, captchaToken);
      setAdminToken(token);
      toast.success("Welcome to Vehicle Management");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
      setCaptchaToken("");
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-8 shadow-sm"
        data-testid="admin-login-form"
      >
        <div className="mb-6 flex justify-center">
          <IvpLogo />
        </div>
        <div className="mb-6 flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#5C2D91]" />
          <h1 className="font-heading text-lg font-bold text-[#0F172A]">Vehicle Management</h1>
        </div>
        <label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-[#0F172A]">
          Admin password
        </label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter password"
          className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base outline-none transition-all focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]"
          data-testid="admin-password-input"
        />
        {captchaEnabled && (
          <div className="mt-4" data-testid="admin-captcha">
            <TurnstileWidget
              ref={captchaRef}
              onToken={setCaptchaToken}
              onExpired={() => setCaptchaToken("")}
            />
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-3 font-semibold text-white transition-all hover:bg-[#4A2475] active:scale-[0.98] disabled:opacity-60"
          data-testid="admin-login-button"
        >
          {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in…</> : "Sign in"}
        </button>
      </form>
    </div>
  );
};
